<template>
  <div class="employment-action-column">
    <WdsMenu z-index="1000" placement="bottom-end">
      <template #trigger>
        <WdsButtonGhost :label="$t('More options')" data-test="employment-actions-menu">
          <WdsIcon name="overflow-menu-horizontal" />
        </WdsButtonGhost>
      </template>

      <WdsMenuOption data-test="see-worker-details-option" @click="seeWorkerDetails">
        <WdsStack direction-row align-center gap-sm>
          <WdsIcon name="person" :size="16" />
          {{ $t('See worker details') }}
        </WdsStack>
      </WdsMenuOption>

      <WdsMenuDivider />

      <WdsMenuOption
        theme="SUCCESS"
        data-test="onboard-employment-button"
        :disabled="loading"
        @click="onboard"
      >
        <WdsStack direction-row align-center gap-sm>
          <WdsIcon v-if="!loading" name="hire" :size="16" />
          <WdsSpinner v-else :size="16" />
          {{ loading ? $t('Loading...') : $t('Start onboarding') }}
        </WdsStack>
      </WdsMenuOption>
    </WdsMenu>

    <EmploymentOnboardModal
      :key="`onboard-modal-${column.id}`"
      v-model:show="showOnboardModal"
      :employment="modalData"
      @onboarded="$emit('employment-onboarded')"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import {
  WdsButtonGhost,
  WdsIcon,
  WdsMenu,
  WdsMenuDivider,
  WdsMenuOption,
  WdsSpinner,
  WdsStack,
} from '@worksome/wds-components'
import { useLazyQuery } from '@vue/apollo-composable'
import { FragmentType, graphql, useFragment } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import EmploymentOnboardModal from '@/components/Employments/Partner/EmploymentOnboardModal.vue'
import { useTablePanels } from '@/components/GraphqlDataTable/useTablePanels'

const { openPartnerWorkerDetailPanel } = useTablePanels()

const EmploymentActionColumnNew = graphql(`
  fragment EmploymentActionColumnNew on Employment {
    id
    worker {
      id
    }
  }
`)

const props = defineProps<{
  row: FragmentType<typeof EmploymentActionColumnNew>
}>()

defineEmits<{
  'employment-onboarded': []
}>()

const showOnboardModal = ref(false)

const column = computed(() => useFragment(EmploymentActionColumnNew, props.row))

const { result, loading, load, refetch, onResult } = useLazyQuery(
  graphql(`
    query EmploymentOnboardModalQuery($id: ID!) {
      employment(id: $id) {
        ...EmploymentOnboardModal
      }
    }
  `),
  () => ({ id: column.value.id }),
  {
    clientId: 'publicApi',
    fetchPolicy: 'network-only',
  },
)

const modalData = computed(() => result.value?.employment ?? null)

onResult((queryResult) => {
  if (queryResult.data?.employment) {
    showOnboardModal.value = true
  }
})

const seeWorkerDetails = () => {
  openPartnerWorkerDetailPanel({
    workerId: column.value.worker.id,
    employmentId: column.value.id,
  })
}

const onboard = () => {
  if (result.value) {
    refetch()
  } else {
    load()
  }
}
</script>

<style scoped lang="scss">
.employment-action-column {
  :deep(.wds-menu) {
    width: max-content;
  }
}
</style>
